// src/components/AdminAuthGate.js
// ============================================================
// 後台登入閘門：包住任何需要管理員登入的路由
// 帳號密碼透過 /api/admin-auth/login 驗證
// token 存在 localStorage，24 小時到期自動登出
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const STORAGE_KEY = 'admin_token';

export default function AdminAuthGate({ children }) {
  const [status, setStatus] = useState('checking'); // checking | login | ok
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 驗證現有 token
  const verify = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) { setStatus('login'); return; }
    try {
      await axios.get('/api/admin-auth/verify', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus('ok');
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      setStatus('login');
    }
  }, []);

  useEffect(() => { verify(); }, [verify]);

  // 登入
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post('/api/admin-auth/login', { username, password });
      if (data.ok && data.token) {
        localStorage.setItem(STORAGE_KEY, data.token);
        setStatus('ok');
      } else {
        setError(data.error || '登入失敗');
      }
    } catch (err) {
      setError(err?.response?.data?.error || '登入失敗，請確認帳號密碼');
    } finally {
      setLoading(false);
    }
  };

  // 登出
  window.__adminLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setStatus('login');
    setUsername('');
    setPassword('');
  };

  // 檢查中
  if (status === 'checking') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={{ textAlign: 'center', color: '#666' }}>驗證中...</p>
        </div>
      </div>
    );
  }

  // 需要登入
  if (status === 'login') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>🔒</div>
          <h2 style={styles.title}>後台管理登入</h2>
          <form onSubmit={handleLogin}>
            <div style={styles.field}>
              <label style={styles.label}>帳號</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="請輸入帳號"
                style={styles.input}
                autoComplete="username"
                autoFocus
                required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>密碼</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="請輸入密碼"
                style={styles.input}
                autoComplete="current-password"
                required
              />
            </div>
            {error && <div style={styles.error}>{error}</div>}
            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.btn,
                opacity: loading ? 0.6 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? '登入中...' : '登入'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // 已登入 → 顯示子元件
  return children;
}

// 提供一個登出函式供其他元件呼叫
export function adminLogout() {
  localStorage.removeItem(STORAGE_KEY);
  window.location.reload();
}

export function getAdminToken() {
  return localStorage.getItem(STORAGE_KEY);
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: 20,
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '40px 36px',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  logo: {
    textAlign: 'center',
    fontSize: 48,
    marginBottom: 8,
  },
  title: {
    textAlign: 'center',
    fontSize: 22,
    fontWeight: 700,
    color: '#1a1a2e',
    marginBottom: 28,
  },
  field: {
    marginBottom: 18,
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    color: '#333',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    border: '2px solid #e0e0e0',
    borderRadius: 10,
    outline: 'none',
    transition: 'border-color .2s',
    boxSizing: 'border-box',
  },
  error: {
    color: '#e53935',
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'center',
    marginBottom: 12,
  },
  btn: {
    width: '100%',
    padding: '14px',
    fontSize: 16,
    fontWeight: 700,
    color: '#fff',
    background: 'linear-gradient(135deg, #1a1a2e, #0f3460)',
    border: 'none',
    borderRadius: 10,
    marginTop: 8,
  },
};
